import type { DateSelectorPresetId } from '@app/components/date-range-selector/single-date/date-selector-presets';
import {
  getPresetDate,
  getPresetIdFromDate,
  isCustomDateSelectorPreset,
} from '@app/components/date-range-selector/single-date/date-selector-presets';
import { SingleDateSelector } from '@app/components/date-range-selector/single-date/SingleDateSelector';
import { useTimezone } from '@app/core/hooks/useTimezone';
import ClearIcon from '@mui/icons-material/Clear';
import { Box, IconButton, Theme } from '@mui/material';
import { SystemStyleObject } from '@mui/system';
import moment from 'moment-timezone';
import { GenericParser, useQueryState } from 'nuqs';
import { ComponentProps } from 'react';
import { MessageDescriptor, useIntl } from 'react-intl';
import { useListViewContext } from '../context/ListViewContext';
import type { SingleDateFilterValue } from '../helpers/parse-as-single-date-filter';

/**
 * Props for the generic single-date filter component.
 */
export interface GenericSingleDateFilterProps {
  /** The URL query parameter key */
  name: string;
  /** Message descriptor for the label (shown above the selector). Pass false to hide the label. */
  label: MessageDescriptor | false;
  /** Parser for reading/writing this filter value from/to URL (from FilterDefinition). */
  parser: GenericParser<SingleDateFilterValue | null>;
  /** Whether the filter is active (addable filters). Undefined for permanent filters. */
  isActive?: boolean;
  /** Callback when filter is cleared (addable filters). */
  onRemove?: () => void;
  /** Custom styles for the container */
  sx?: SystemStyleObject<Theme>;
  /** Disallow selecting dates after today. Default true. */
  disableFutureDates?: boolean;
  /** How many years back the calendar month picker allows. Default 1. */
  dayPickerHistoryYears?: number;
  /** Props passed to the SingleDateSelector trigger button. */
  buttonProps?: Partial<ComponentProps<typeof SingleDateSelector>['buttonProps']>;
}

/**
 * Derives display date and preset id for SingleDateSelector from the URL filter value.
 */
function filterValueToSelectorState(
  urlValue: SingleDateFilterValue | null,
  timezone: string,
): { dateValue: Date | null; presetId: DateSelectorPresetId | null } {
  if (!urlValue || !timezone) {
    return { dateValue: null, presetId: null };
  }

  let dateValue: Date | null;
  if (isCustomDateSelectorPreset(urlValue.presetId) && urlValue.date) {
    dateValue = moment(urlValue.date).tz(timezone).startOf('day').toDate();
  } else {
    dateValue = getPresetDate(urlValue.presetId, timezone);
  }

  const presetId =
    dateValue != null ? getPresetIdFromDate(dateValue, timezone) : (urlValue.presetId ?? null);

  return { dateValue, presetId };
}

/**
 * Generic single-date filter for ListView. Uses SingleDateSelector; filter value is preset-based
 * (presetId; for custom selection also date).
 */
export function GenericSingleDateFilter({
  name,
  label,
  parser,
  isActive,
  onRemove,
  sx,
  disableFutureDates = true,
  dayPickerHistoryYears = 1,
  buttonProps,
}: GenericSingleDateFilterProps) {
  const { $t } = useIntl();
  const tz = useTimezone();
  const { setFilterValue } = useListViewContext();

  const [urlValue] = useQueryState(name, parser) as [SingleDateFilterValue | null, never];
  const { dateValue, presetId } = filterValueToSelectorState(urlValue, tz);

  const handleChange = (date: Date, newPresetId: DateSelectorPresetId) => {
    const next: SingleDateFilterValue = {
      presetId: newPresetId,
      ...(isCustomDateSelectorPreset(newPresetId) && {
        date: moment(date).tz(tz).startOf('day').format('YYYY-MM-DD'),
      }),
    };
    setFilterValue(name, next);
  };

  const handleClear = () => {
    setFilterValue(name, null);
  };

  if (isActive === false && !urlValue) {
    return null;
  }

  const hasValue = urlValue != null;
  const showClear = hasValue && onRemove != null;

  // Bound "today" and month span in the station timezone so the calendar matches station days.
  const nowInTz = moment().tz(tz);
  const dayPickerDisabled = disableFutureDates
    ? { after: nowInTz.clone().endOf('day').toDate() }
    : undefined;
  const dayPickerStartMonth = nowInTz
    .clone()
    .subtract(dayPickerHistoryYears, 'year')
    .startOf('month')
    .toDate();
  const dayPickerEndMonth = nowInTz.clone().endOf('month').toDate();

  return (
    <Box sx={[sx ?? {}]}>
      {label !== false && (
        <Box
          component="span"
          sx={{
            display: 'block',
            typography: 'caption',
            color: 'text.secondary',
            mb: 0.5,
          }}
        >
          {$t(label)}
        </Box>
      )}
      <Box sx={{ position: 'relative' }}>
        <SingleDateSelector
          value={dateValue ?? undefined}
          presetId={presetId ?? undefined}
          onChange={handleChange}
          dayPickerDisabled={dayPickerDisabled}
          dayPickerStartMonth={dayPickerStartMonth}
          dayPickerEndMonth={dayPickerEndMonth}
          buttonProps={{
            variant: 'outlined',
            color: 'secondary',
            ...buttonProps,
            sx: {
              ...(buttonProps?.sx ?? {}),
              minWidth: '18ch',
              textTransform: 'none',
            },
          }}
        />
        {showClear && (
          <IconButton
            aria-label={$t({
              id: 'common.clear',
              defaultMessage: 'Clear',
            })}
            onClick={handleClear}
            edge="end"
            size="small"
            disabled={!hasValue}
            sx={{
              position: 'absolute',
              right: 4,
              top: '50%',
              transform: 'translateY(-50%)',
              ...(!hasValue ? { visibility: 'hidden', pointerEvents: 'none' } : {}),
            }}
          >
            <ClearIcon fontSize="small" />
          </IconButton>
        )}
      </Box>
    </Box>
  );
}
